import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type Classroom, type TeacherAccount } from '../../../accounts';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'teacher',
    updatePassword
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-7251">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-7252">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-7254">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7255" /> Bewerk
              </Button> : null} data-id="element-7253">
          <div className="space-y-6" data-id="element-7256">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7257">
                <div data-id="element-7258">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7259">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-7260" />
                </div>
                <div data-id="element-7261">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7262">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-7263" />
                </div>
                <div className="sm:col-span-2" data-id="element-7264">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7265">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-7266" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-7267">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-7268">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-7269">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7270">
                  <div data-id="element-7271">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7272">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7273">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-7274">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7275">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7276">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-7277">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-7278">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-7279">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-7280">
                      <School className="w-5 h-5" data-id="element-7281" />
                    </div>
                    <div data-id="element-7282">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-7283">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-7284">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-7286">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7287" /> Wijzig
              </Button> : null} data-id="element-7285">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-7288">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7289">
                <div className="sm:col-span-2" data-id="element-7290">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7291">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-7292" />
                </div>
                <div data-id="element-7293">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7294">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-7295" />
                </div>
                <div data-id="element-7296">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7297">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-7298" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-7299">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7300" />
                  <p data-id="element-7301">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-7302">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7303" />
                  <p data-id="element-7304">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-7305">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-7306">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-7307">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-7308">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Klassen Overzicht */}
        <SettingsSection title="Klassen Overzicht" description="Je actieve klassen" icon={Users} accentColor="bg-blue-100 text-blue-600" action={<Button onClick={() => onNavigate?.('management')} variant="ghost" size="sm" data-id="element-7310">
              Beheer klassen
            </Button>} data-id="element-7309">
          {classrooms.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7311">
              {classrooms.map(c => <div key={c.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-7312">
                  <div className="font-bold text-slate-900" data-id="element-7313">{c.name}</div>
                  <div className="text-sm text-slate-500 mt-1" data-id="element-7314">
                    Groep {c.groupLevel} • {c.learnerIds.length} leerlingen
                  </div>
                </div>)}
            </div> : <div className="text-sm text-slate-500" data-id="element-7315">
              Je hebt nog geen klassen.
            </div>}
        </SettingsSection>
      </div>
    </PageLayout>;
}